import React, { useState, useRef } from 'react';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { filesAPI } from '../../services/api';
import {
  Gauge, Upload, Loader2, AlertTriangle, Check, Download,
  BarChart3, Zap, Activity, FileText, ChevronDown, ChevronUp, Image, Brain
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// ======================== DYNO CHART COMPONENT ========================
const DynoChart = ({ data, title, beforeAfter = false }) => {
  if (!data?.length) return null;
  return (
    <div className="w-full h-[350px] min-w-[200px]" data-testid="dyno-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="rpm" stroke="#64748b" tick={{ fontSize: 11 }} label={{ value: 'RPM', position: 'insideBottom', offset: -5, fill: '#64748b' }} />
          <YAxis yAxisId="ps" stroke="#EC2229" tick={{ fontSize: 11 }} label={{ value: 'PS', angle: -90, position: 'insideLeft', fill: '#EC2229' }} />
          <YAxis yAxisId="nm" orientation="right" stroke="#22d3ee" tick={{ fontSize: 11 }} label={{ value: 'Nm', angle: 90, position: 'insideRight', fill: '#22d3ee' }} />
          <Tooltip contentStyle={{ backgroundColor: '#1A1D23', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {beforeAfter ? (
            <>
              <Line yAxisId="ps" type="monotone" dataKey="before_ps" name="Vorher PS" stroke="#666" strokeWidth={2} dot={false} strokeDasharray="5 5" />
              <Line yAxisId="ps" type="monotone" dataKey="after_ps" name="Nachher PS" stroke="#EC2229" strokeWidth={2.5} dot={false} />
              <Line yAxisId="nm" type="monotone" dataKey="before_nm" name="Vorher Nm" stroke="#555" strokeWidth={2} dot={false} strokeDasharray="5 5" />
              <Line yAxisId="nm" type="monotone" dataKey="after_nm" name="Nachher Nm" stroke="#22d3ee" strokeWidth={2.5} dot={false} />
            </>
          ) : (
            <>
              <Line yAxisId="ps" type="monotone" dataKey="power_ps" name="Leistung (PS)" stroke="#EC2229" strokeWidth={2.5} dot={false} />
              <Line yAxisId="nm" type="monotone" dataKey="torque_nm" name="Drehmoment (Nm)" stroke="#22d3ee" strokeWidth={2.5} dot={false} />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ======================== LOG EVALUATION COMPONENT ========================
const LogEvaluation = ({ evaluation }) => {
  const [expanded, setExpanded] = useState(false);
  if (!evaluation) return null;

  const statusColor = {
    ok: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    warning: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    critical: 'text-red-400 bg-red-500/10 border-red-500/20',
  };

  return (
    <div className="glass-card p-5" data-testid="log-evaluation">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#EC2229]" />
          Log-Bewertung
        </h3>
        <div className={`px-3 py-1 rounded-full border text-xs font-semibold ${statusColor[evaluation.overall_status]}`}>
          {evaluation.overall_status === 'ok' ? 'Alle Werte OK' : evaluation.overall_status === 'warning' ? 'Warnungen' : 'Kritisch'}
        </div>
      </div>

      {evaluation.warnings?.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {evaluation.warnings.map((w, i) => (
            <div key={i} className={`p-2 rounded-lg text-xs flex items-start gap-2 ${w.severity === 'critical' ? 'bg-red-500/10 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
              <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${w.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`} />
              <span className={w.severity === 'critical' ? 'text-red-300' : 'text-amber-300'}>{w.message}</span>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setExpanded(!expanded)} className="text-xs text-slate-500 hover:text-white flex items-center gap-1">
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? 'Weniger anzeigen' : 'Alle Messwerte anzeigen'}
      </button>

      {expanded && (
        <div className="mt-3 space-y-1.5">
          {Object.entries(evaluation.stats || {}).map(([col, stat]) => (
            <div key={col} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02] text-xs">
              <span className="text-slate-400 truncate flex-1 mr-2">{col}</span>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-slate-500">Min: <strong className="text-white">{stat.min}{stat.unit}</strong></span>
                <span className="text-slate-500">Max: <strong className="text-white">{stat.max}{stat.unit}</strong></span>
                <span className="text-slate-500">Avg: <strong className="text-white">{stat.avg}{stat.unit}</strong></span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColor[stat.status]}`}>{stat.status.toUpperCase()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ======================== AI EVALUATION COMPONENT ========================
const AiEvaluation = ({ aiResult }) => {
  if (!aiResult) return null;
  return (
    <div className="glass-card p-5 border-purple-500/20 bg-purple-500/5" data-testid="ai-evaluation">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Brain className="w-4 h-4 text-purple-400" /> KI-Bewertung (GPT-5.2)
      </h3>
      {aiResult.success ? (
        <div className="prose prose-invert prose-sm max-w-none text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
          {aiResult.ai_evaluation}
        </div>
      ) : (
        <div className="text-sm text-red-400">{aiResult.error || 'KI-Bewertung fehlgeschlagen'}</div>
      )}
    </div>
  );
};

// ======================== MAIN PAGE ========================
const VirtualDynoPage = () => {
  const [mode, setMode] = useState('single'); // 'single' or 'compare'
  const [logFile, setLogFile] = useState(null);
  const [logBefore, setLogBefore] = useState(null);
  const [logAfter, setLogAfter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState('');
  const chartRef = useRef(null);

  const handleAnalyze = async () => {
    if (!logFile) return;
    setLoading(true); setError(''); setResult(null); setEvaluation(null); setAiResult(null);
    try {
      const fd = new FormData();
      fd.append('log_file', logFile);
      const [dynoRes, evalRes] = await Promise.all([
        filesAPI.calculateDyno(fd),
        (async () => { const fd2 = new FormData(); fd2.append('log_file', logFile); return filesAPI.evaluateLog(fd2); })(),
      ]);
      setResult(dynoRes.data);
      setEvaluation(evalRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Analyse fehlgeschlagen');
    } finally { setLoading(false); }
  };

  const handleCompare = async () => {
    if (!logBefore || !logAfter) return;
    setLoading(true); setError(''); setCompareResult(null);
    try {
      const fd = new FormData();
      fd.append('log_before', logBefore);
      fd.append('log_after', logAfter);
      const res = await filesAPI.compareDyno(fd);
      setCompareResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Vergleich fehlgeschlagen');
    } finally { setLoading(false); }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCustomLogo(file);
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAiEvaluate = async () => {
    if (!logFile) return;
    setAiLoading(true); setAiResult(null);
    try {
      const fd = new FormData();
      fd.append('log_file', logFile);
      fd.append('vehicle_info', vehicleInfo);
      const res = await filesAPI.aiEvaluateLog(fd);
      setAiResult(res.data);
    } catch (err) {
      setAiResult({ success: false, error: err.response?.data?.detail || 'KI-Bewertung fehlgeschlagen' });
    } finally { setAiLoading(false); }
  };

  const exportPDF = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current, { backgroundColor: '#0D0D0D', scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('landscape', 'mm', 'a4');

    // Header with logo
    if (logoPreview) {
      pdf.addImage(logoPreview, 'PNG', 10, 8, 40, 15);
    }
    pdf.setFontSize(18);
    pdf.setTextColor(236, 34, 41);
    pdf.text('Virtual Dyno Report', logoPreview ? 55 : 10, 18);
    pdf.setFontSize(9);
    pdf.setTextColor(150, 150, 150);
    pdf.text(`Erstellt am ${new Date().toLocaleDateString('de-DE')}`, logoPreview ? 55 : 10, 24);

    // Chart
    const chartW = 270; const chartH = 130;
    pdf.addImage(imgData, 'PNG', 10, 30, chartW, chartH);

    // Peak values
    const peak = mode === 'single' ? result?.peak : null;
    const gains = mode === 'compare' ? compareResult?.gains : null;
    pdf.setFontSize(11); pdf.setTextColor(255, 255, 255);
    let yPos = 168;
    if (peak) {
      pdf.text(`Maximale Leistung: ${peak.power_ps} PS bei ${peak.peak_power_rpm} RPM`, 10, yPos);
      pdf.text(`Maximales Drehmoment: ${peak.torque_nm} Nm bei ${peak.peak_torque_rpm} RPM`, 10, yPos + 7);
    }
    if (gains) {
      pdf.text(`Leistungsgewinn: +${gains.power_ps} PS / +${gains.torque_nm} Nm`, 10, yPos);
    }

    pdf.setFontSize(7); pdf.setTextColor(100, 100, 100);
    pdf.text('Virtual Dyno — berechnet aus OBD-Logdaten. Werte sind Richtwerte (ca. +/-5-10% vs. Prüfstand).', 10, 200);

    pdf.save(`dyno_report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="virtual-dyno-page">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white flex items-center gap-3">
            <Gauge className="w-6 h-6 text-[#EC2229]" />
            Virtual Dyno
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Leistungsdiagramm aus OBD-Logdaten berechnen</p>
        </div>

        {/* Mode Selector */}
        <div className="flex gap-2">
          {[
            { id: 'single', label: 'Einzelanalyse', icon: BarChart3 },
            { id: 'compare', label: 'Vorher / Nachher', icon: Zap },
          ].map(m => (
            <button key={m.id} onClick={() => { setMode(m.id); setResult(null); setCompareResult(null); setEvaluation(null); setError(''); setLogFile(null); setLogBefore(null); setLogAfter(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${mode === m.id ? 'bg-[#EC2229]/10 text-[#EC2229] border border-[#EC2229]/30' : 'bg-white/5 text-slate-400 border border-white/10 hover:border-white/20'}`}
              data-testid={`mode-${m.id}`}>
              <m.icon className="w-4 h-4" /> {m.label}
            </button>
          ))}
        </div>

        {/* Upload Section */}
        {mode === 'single' ? (
          <div className="glass-card p-5">
            <Label className="label-text mb-2 block">Datalog (CSV)</Label>
            <p className="text-xs text-slate-500 mb-3">Autotuner-Format, VCDS oder generisches CSV mit RPM + Torque-Spalten</p>
            <Input type="file" accept=".csv,.log,.txt" onChange={e => setLogFile(e.target.files?.[0])}
              className="input-field text-sm file:mr-3 file:text-xs file:border-0 file:bg-[#EC2229]/20 file:text-[#EC2229] file:rounded-md file:px-3 file:py-1" data-testid="dyno-log-file" />
            {logFile && <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1"><Check className="w-3 h-3" /> {logFile.name}</p>}
            <div className="mt-3">
              <Label className="label-text text-xs mb-1.5 block">Fahrzeug (optional, für KI-Bewertung)</Label>
              <Input type="text" placeholder="z.B. BMW 320d F30 Stage 1" value={vehicleInfo} onChange={e => setVehicleInfo(e.target.value)}
                className="input-field text-sm" data-testid="vehicle-info-input" />
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={handleAnalyze} disabled={!logFile || loading} className="btn-primary" data-testid="analyze-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysiere...</> : <><Gauge className="w-4 h-4 mr-2" /> Dyno berechnen</>}
              </Button>
              <Button onClick={handleAiEvaluate} disabled={!logFile || aiLoading} variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10" data-testid="ai-evaluate-btn">
                {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> KI analysiert...</> : <><Brain className="w-4 h-4 mr-2" /> KI-Bewertung</>}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="glass-card p-5">
              <Label className="label-text mb-2 block">Vorher-Log (Original)</Label>
              <Input type="file" accept=".csv,.log,.txt" onChange={e => setLogBefore(e.target.files?.[0])}
                className="input-field text-sm file:mr-3 file:text-xs file:border-0 file:bg-slate-500/20 file:text-slate-400 file:rounded-md file:px-3 file:py-1" data-testid="dyno-log-before" />
              {logBefore && <p className="text-xs text-slate-400 mt-1">{logBefore.name}</p>}
            </div>
            <div className="glass-card p-5">
              <Label className="label-text mb-2 block">Nachher-Log (Tuned)</Label>
              <Input type="file" accept=".csv,.log,.txt" onChange={e => setLogAfter(e.target.files?.[0])}
                className="input-field text-sm file:mr-3 file:text-xs file:border-0 file:bg-[#EC2229]/20 file:text-[#EC2229] file:rounded-md file:px-3 file:py-1" data-testid="dyno-log-after" />
              {logAfter && <p className="text-xs text-emerald-400 mt-1">{logAfter.name}</p>}
            </div>
            <div className="md:col-span-2">
              <Button onClick={handleCompare} disabled={!logBefore || !logAfter || loading} className="btn-primary" data-testid="compare-dyno-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Vergleiche...</> : <><Zap className="w-4 h-4 mr-2" /> Vergleichen</>}
              </Button>
            </div>
          </div>
        )}

        {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}

        {/* Single Analysis Result */}
        {result && mode === 'single' && (
          <div className="space-y-4">
            {/* Peak Values */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="glass-card p-4 text-center">
                <p className="text-[10px] text-slate-500 uppercase mb-1">Max. Leistung</p>
                <p className="text-3xl font-heading font-bold text-[#EC2229]">{result.peak?.power_ps}</p>
                <p className="text-xs text-slate-400">PS bei {result.peak?.peak_power_rpm} RPM</p>
              </div>
              <div className="glass-card p-4 text-center">
                <p className="text-[10px] text-slate-500 uppercase mb-1">Max. Drehmoment</p>
                <p className="text-3xl font-heading font-bold text-cyan-400">{result.peak?.torque_nm}</p>
                <p className="text-xs text-slate-400">Nm bei {result.peak?.peak_torque_rpm} RPM</p>
              </div>
              <div className="glass-card p-4 text-center">
                <p className="text-[10px] text-slate-500 uppercase mb-1">Max. Drehzahl</p>
                <p className="text-2xl font-heading font-bold text-white">{result.peak?.rpm_max}</p>
                <p className="text-xs text-slate-400">RPM</p>
              </div>
              <div className="glass-card p-4 text-center">
                <p className="text-[10px] text-slate-500 uppercase mb-1">Datenpunkte</p>
                <p className="text-2xl font-heading font-bold text-white">{result.total_data_points}</p>
                <p className="text-xs text-slate-400">Messwerte</p>
              </div>
            </div>

            {/* Chart */}
            <div className="glass-card p-5" ref={chartRef}>
              <h3 className="text-sm font-semibold text-white mb-2">Leistungsdiagramm</h3>
              <DynoChart data={result.power_curve} />
            </div>

            {/* Log Evaluation */}
            <LogEvaluation evaluation={evaluation} />

            {/* AI Evaluation */}
            <AiEvaluation aiResult={aiResult} />

            {/* Export */}
            <div className="glass-card p-5">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Download className="w-4 h-4 text-[#EC2229]" /> Export
              </h3>
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <Label className="label-text text-xs mb-1 block">Eigenes Logo (optional)</Label>
                  <Input type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={handleLogoUpload}
                    className="input-field text-xs w-48 file:mr-2 file:text-[10px] file:border-0 file:bg-white/10 file:text-slate-400 file:rounded file:px-2 file:py-0.5" data-testid="logo-upload" />
                </div>
                {logoPreview && <img src={logoPreview} alt="Logo" className="h-8 object-contain" />}
                <Button onClick={exportPDF} className="btn-primary" data-testid="export-pdf-btn">
                  <Download className="w-4 h-4 mr-2" /> PDF exportieren
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Compare Result */}
        {compareResult && mode === 'compare' && (
          <div className="space-y-4">
            {/* Gains */}
            <div className="glass-card p-6 bg-[#EC2229]/5 border-[#EC2229]/20">
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Leistungsgewinn</p>
                  <p className={`text-4xl font-heading font-bold ${compareResult.gains.power_ps > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {compareResult.gains.power_ps > 0 ? '+' : ''}{compareResult.gains.power_ps}
                  </p>
                  <p className="text-xs text-slate-400">PS ({compareResult.gains.power_kw > 0 ? '+' : ''}{compareResult.gains.power_kw} kW)</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Drehmomentgewinn</p>
                  <p className={`text-4xl font-heading font-bold ${compareResult.gains.torque_nm > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {compareResult.gains.torque_nm > 0 ? '+' : ''}{compareResult.gains.torque_nm}
                  </p>
                  <p className="text-xs text-slate-400">Nm</p>
                </div>
                <div className="flex flex-col justify-center">
                  <div className="text-xs text-slate-400 space-y-1">
                    <p>Vorher: <strong className="text-white">{compareResult.before?.peak?.power_ps} PS / {compareResult.before?.peak?.torque_nm} Nm</strong></p>
                    <p>Nachher: <strong className="text-[#EC2229]">{compareResult.after?.peak?.power_ps} PS / {compareResult.after?.peak?.torque_nm} Nm</strong></p>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart */}
            <div className="glass-card p-5" ref={chartRef}>
              <h3 className="text-sm font-semibold text-white mb-2">Vorher / Nachher Vergleich</h3>
              <DynoChart data={compareResult.combined_curve} beforeAfter />
            </div>

            {/* Export */}
            <div className="glass-card p-5">
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <Label className="label-text text-xs mb-1 block">Eigenes Logo</Label>
                  <Input type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={handleLogoUpload}
                    className="input-field text-xs w-48 file:mr-2 file:text-[10px] file:border-0 file:bg-white/10 file:text-slate-400 file:rounded file:px-2 file:py-0.5" />
                </div>
                {logoPreview && <img src={logoPreview} alt="Logo" className="h-8 object-contain" />}
                <Button onClick={exportPDF} className="btn-primary">
                  <Download className="w-4 h-4 mr-2" /> PDF exportieren
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="glass-card p-4 bg-blue-500/5 border-blue-500/10">
          <h4 className="text-xs font-semibold text-blue-400 mb-2">Hinweis zur Virtual Dyno</h4>
          <ul className="text-[10px] text-slate-400 space-y-1">
            <li>Die Berechnung erfolgt über: Leistung (kW) = Drehmoment (Nm) x Drehzahl (RPM) / 9549</li>
            <li>Genauigkeit ca. +/-5-10% vs. echter Prüfstand (OBD-Werte haben 10-50ms Latenz)</li>
            <li>Für Vorher/Nachher-Vergleiche unter gleichen Bedingungen ist die Differenz sehr genau</li>
            <li>Benötigte Daten: Engine speed (RPM) und Engine torque (Nm). Für Bewertung zusätzlich: Temperaturen, Lambda, Ladedruck</li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VirtualDynoPage;
